import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
  computed,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BreadcrumbItem,
  KebabMenuItem,
  QaiBreadcrumbListComponent,
  QaiButtonComponent,
  QaiIconComponent,
  QaiLoadingComponent,
  QaiTableComponent,
  QaiBadgeKind,
  HeaderConfig,
  TableRow,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { EditTeamAssociationsModalComponent } from '../components/modals/edit-team-associations/edit-team-associations-modal.component';
import { AddToAssignmentModalComponent } from '../components/modals/add-to-assignment/add-to-assignment-modal.component';
import { IAssignmentOption } from 'src/app/interfaces/self-serve/assignment-manager/responses/iadd-to-assignment';
import { ITeamAssociation, IUserAssignmentSummary, TeamRelationship } from 'src/app/interfaces/self-serve/assignment-manager/responses/iuser-assignment-summary';
import { transformLabel } from 'src/app/utils/string-replacements';
import { localeLowerCase } from 'src/app/utils/locale-case.util';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-user-detail-page',
  standalone: true,
  templateUrl: './user-detail-page.component.html',
  styleUrls: ['./user-detail-page.component.scss'],
  imports: [
    QaiBreadcrumbListComponent,
    QaiButtonComponent,
    QaiIconComponent,
    QaiLoadingComponent,
    QaiTableComponent,
    TranslocoModule,
    EditTeamAssociationsModalComponent,
    AddToAssignmentModalComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class UserDetailPageComponent implements OnInit {
  private readonly route  = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly assignmentsService = inject(AssignmentsService);
  private readonly translocoService   = inject(TranslocoService);
  private readonly destroyRef         = inject(DestroyRef);

  // ── i18n keys ────────────────────────────────────────────────────────────
  readonly i18nKeys = {
    breadcrumbParent: 'simulationBuilder.BREADCRUMB_USERS',
    addToAssignment:  'simulationBuilder.ADD_TO_ASSIGNMENT',
    assignmentsCount: 'simulationBuilder.ASSIGNMENTS_COUNT',
    excludedText:     'simulationBuilder.REPORTING_EXCLUDED',
    includedText:     'simulationBuilder.REPORTING_INCLUDED',
    kebabEditTeams:   'simulationBuilder.KEBAB_EDIT_TEAM_ASSOCIATIONS',
    kebabExclude:     'simulationBuilder.KEBAB_EXCLUDE_FROM_REPORTING',
    kebabInclude:     'simulationBuilder.KEBAB_INCLUDE_IN_REPORTING',
    kebabRemove:      'simulationBuilder.KEBAB_REMOVE_FROM_ASSIGNMENT',
    viewAssignment:   'simulationBuilder.COL_VIEW_ASSIGNMENT',
    noTeams:          'simulationBuilder.NO_TEAMS',
    teamsCount:       'simulationBuilder.TEAMS_COUNT',
    colName:          'simulationBuilder.COL_ASSIGNMENT_NAME',
    colStatus:        'simulationBuilder.COL_STATUS',
    colTeams:         'simulationBuilder.COL_TEAM_ASSOCIATIONS',
    colReport:        'simulationBuilder.COL_REPORTING_STATUS',
    statusStaged:     'simulationBuilder.STATUS_STAGED',
    statusActive:     'simulationBuilder.STATUS_ACTIVE',
    statusEnded:      'simulationBuilder.STATUS_ENDED',
    statusClosed:     'simulationBuilder.STATUS_CLOSED',
    statusDraft:      'simulationBuilder.STATUS_DRAFT',
    previousLabel:    'simulationBuilder.PAGINATION_PREVIOUS',
    nextLabel:        'simulationBuilder.PAGINATION_NEXT',
  };

  // ── Column keys — set after translation scope loads in ngOnInit
  COL_NAME   = '';
  COL_STATUS = '';
  COL_TEAMS  = '';
  COL_REPORT = '';
  COL_VIEW   = '';

  readonly headers = signal<HeaderConfig[]>([]);
  readonly loadingSize = 'medium';

  readonly tableRef = viewChild<QaiTableComponent>('assignmentsTable');

  // ── State ────────────────────────────────────────────────────────────────
  readonly isLoading        = signal(true);
  readonly isTooltipLoading = signal(false);
  readonly assignments      = signal<IUserAssignmentSummary[]>([]);
  readonly tableData        = computed<TableRow[]>(() => this.assignments().map(a => this.toTableRow(a)));

  readonly firstName = signal('');
  readonly lastName  = signal('');
  readonly email     = signal('');

  readonly userUuid    = signal('');
  readonly accountUuid = signal('');

  readonly fullName = computed(() =>
    `${this.firstName()} ${this.lastName()}`.trim()
  );
  readonly assignmentCount = computed(() => this.assignments().length);

  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => [
    {
      text: this.translocoService.translate(this.i18nKeys.breadcrumbParent),
      href: '/self-serve?fromUserDetails=true',
    } as BreadcrumbItem,
    { text: '', active: true },
  ]);

  // ── Modal state — Edit Team Associations ─────────────────────────────────
  readonly editTeamsModalOpen       = signal(false);
  readonly editTeamsAssignmentName  = signal('');
  readonly editTeamsAssignmentUuid  = signal('');
  readonly editTeamsCurrentUuids    = signal<string[]>([]);
  readonly userTeams                = signal<ITeamAssociation[]>([]);

  // ── Modal state — Add to Assignment ──────────────────────────────────────
  readonly addModalOpen        = signal(false);
  readonly addModalAssignments = signal<IAssignmentOption[]>([]);
  readonly addModalLoading     = signal(false);

  ngOnInit(): void {
    this.userUuid.set(this.route.snapshot.paramMap.get('uuid') ?? '');
    this.accountUuid.set(SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid());

    const state = history.state ?? {};
    this.firstName.set(state['firstName'] ?? '');
    this.lastName.set(state['lastName']   ?? '');
    this.email.set(state['email']         ?? '');

    this.translocoService.selectTranslation('simulationBuilder').pipe(take(1)).subscribe(() => {
      this.COL_NAME   = this.translocoService.translate(this.i18nKeys.colName);
      this.COL_STATUS = this.translocoService.translate(this.i18nKeys.colStatus);
      this.COL_TEAMS  = this.translocoService.translate(this.i18nKeys.colTeams);
      this.COL_REPORT = this.translocoService.translate(this.i18nKeys.colReport);

      this.headers.set([
        { name: this.COL_NAME,   sortable: true,  columnWidth: '28%' },
        { name: this.COL_STATUS, sortable: true,  columnWidth: '12%' },
        { name: this.COL_TEAMS,  sortable: true,  columnWidth: '28%' },
        { name: this.COL_REPORT, sortable: true,  columnWidth: '16%' },
        { name: this.COL_VIEW,   sortable: false, columnWidth: '10%' },
      ]);

      this.loadAssignments();
    });
  }

  onAddToAssignment(): void {
    this.addModalLoading.set(true);

    const assignments$ = this.assignmentsService.findAllAssignments(this.accountUuid());
    const teams$       = this.assignmentsService.getAccountTeams(this.accountUuid());

    forkJoin({ assignments: assignments$, teams: teams$ }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ assignments, teams }) => {
        this.addModalAssignments.set(
          (assignments ?? [])
            .map(a => ({ uuid: a.uuid, name: a.name, status: a.status }))
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        );
        this.userTeams.set(
          (teams ?? [])
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(t => ({ uuid: t.uuid, name: t.name, relationship: TeamRelationship.MEMBER }))
        );
        this.addModalLoading.set(false);
        this.addModalOpen.set(true);
      },
      error: () => {
        this.addModalLoading.set(false);
      },
    });
  }

  onAddModalClose(): void {
    this.addModalOpen.set(false);
  }

  onAssigned(payload: { moduleAssignmentUuid: string; teamUuids: string[] }): void {
    this.assignmentsService
      .addUserToAssignment(this.userUuid(), payload.moduleAssignmentUuid, payload.teamUuids, this.accountUuid())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.addModalOpen.set(false);
          this.loadAssignments();
        },
        error: () => {
          this.addModalOpen.set(false);
        },
      });
  }

  onRowAction(event: { action: KebabMenuItem; rowId: string | number; rowData: TableRow; event: Event }): void {
    const locale = this.translocoService.getActiveLang() || 'en';
    const text = localeLowerCase(event.action.text ?? '', locale);

    if (text === localeLowerCase(this.translocoService.translate(this.i18nKeys.kebabEditTeams), locale)) {
      this.openEditTeamsModal(event.rowId as string);
    } else if (text === localeLowerCase(this.translocoService.translate(this.i18nKeys.kebabExclude), locale)) {
      this.toggleReporting(event.rowId as string, true);
    } else if (text === localeLowerCase(this.translocoService.translate(this.i18nKeys.kebabInclude), locale)) {
      this.toggleReporting(event.rowId as string, false);
    } else if (text === localeLowerCase(this.translocoService.translate(this.i18nKeys.kebabRemove), locale)) {
      this.removeFromAssignment(event.rowId as string);
    }
  }

  onRowClick(event: { rowId: string | number; rowData: TableRow; event: Event }): void {
    this.onViewAssignment(event.rowId as string);
  }

  onTooltipDataRequested = ({ row }: { row: TableRow }): void => {
    this.isTooltipLoading.set(true);
    const cell = row[this.COL_TEAMS];
    if (cell?.type !== 'tooltip') {
      this.isTooltipLoading.set(false);
      return;
    }
    setTimeout(() => {
      this.tableRef()?.setTooltipData(cell.value);
      this.isTooltipLoading.set(false);
    }, 300);
  };

  private openEditTeamsModal(moduleAssignmentUuid: string): void {
    const assignment = this.assignments().find(a => a.moduleAssignmentUuid === moduleAssignmentUuid);
    if (!assignment) return;

    this.assignmentsService.getAccountTeams(this.accountUuid()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (teams) => {
        this.userTeams.set(
          (teams ?? [])
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(t => ({ uuid: t.uuid, name: t.name, relationship: TeamRelationship.MEMBER }))
        );
        this.editTeamsAssignmentName.set(assignment.name ?? '');
        this.editTeamsAssignmentUuid.set(moduleAssignmentUuid);
        this.editTeamsCurrentUuids.set((assignment.teamAssociations ?? []).map(t => t.uuid));
        this.editTeamsModalOpen.set(true);
      },
    });
  }

  onEditTeamsModalClose(): void {
    this.editTeamsModalOpen.set(false);
  }

  onTeamAssociationsSaved(teamUuids: string[]): void {
    this.editTeamsModalOpen.set(false);
    this.assignmentsService
      .updateUserTeamAssociations(
        this.userUuid(),
        this.editTeamsAssignmentUuid(),
        teamUuids,
        this.accountUuid()
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.loadAssignments() });
  }

  private toggleReporting(moduleAssignmentUuid: string, exclude: boolean): void {
    this.assignmentsService
      .excludeFromReporting(moduleAssignmentUuid, this.accountUuid(), this.userUuid(), exclude)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.loadAssignments() });
  }

  private removeFromAssignment(moduleAssignmentUuid: string): void {
    this.assignmentsService
      .removeUserFromAssignment(this.userUuid(), moduleAssignmentUuid, this.accountUuid())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.loadAssignments() });
  }

  onViewAssignment(moduleAssignmentUuid: string): void {
    this.router.navigate(['../../assignments', moduleAssignmentUuid], { relativeTo: this.route });
  }

  private loadAssignments(): void {
    if (!this.userUuid() || !this.accountUuid()) {
      this.isLoading.set(false);
      return;
    }
    this.isLoading.set(true);
    this.assignmentsService
      .getUserAssignmentsInAccount(this.userUuid(), this.accountUuid())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: IUserAssignmentSummary[]) => {
          const sorted = [...(data ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
          this.assignments.set(sorted);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false),
      });
  }

  private toTableRow(a: IUserAssignmentSummary): TableRow {
    const allTeams = a.teamAssociations ?? [];
    const uniqueTeamCount = new Set(allTeams.map(t => t.uuid)).size;
    const teamsCell = uniqueTeamCount > 1
      ? {
          type: 'tooltip',
          value: allTeams.map(t => `${t.name} – ${transformLabel(t.relationship)}`),
          label: this.translocoService.translate(this.i18nKeys.teamsCount, { count: uniqueTeamCount }),
        }
      : {
          type: 'text',
          value: allTeams.length >= 1
            ? `${allTeams[0].name} – ${transformLabel(allTeams[0].relationship)}`
            : this.translocoService.translate(this.i18nKeys.noTeams),
        };

    const excluded    = a.excludeReporting ?? false;
    const reportValue = excluded
      ? this.translocoService.translate(this.i18nKeys.excludedText)
      : this.translocoService.translate(this.i18nKeys.includedText);
    const reportKind  = excluded ? 'warning' : 'default';

    const statusBadge = a.status ? this.getStatusBadge(a.status) : null;

    const kebabOptions: KebabMenuItem[] = [
      { type: 'click', text: this.translocoService.translate(this.i18nKeys.kebabEditTeams) },
      { type: 'click', text: excluded
          ? this.translocoService.translate(this.i18nKeys.kebabInclude)
          : this.translocoService.translate(this.i18nKeys.kebabExclude) },
      { type: 'click', text: this.translocoService.translate(this.i18nKeys.kebabRemove) },
    ];

    return {
      id: a.moduleAssignmentUuid,
      [this.COL_NAME]:   { type: 'text',  value: a.name ?? '' },
      [this.COL_STATUS]: statusBadge
        ? { type: 'badge', shape: 'round', kind: statusBadge.kind, label: statusBadge.label }
        : { type: 'text', value: '' },
      [this.COL_TEAMS]:  teamsCell,
      [this.COL_REPORT]: { type: 'text',  value: reportValue, kind: reportKind },
      [this.COL_VIEW]:   { type: 'text',  value: this.translocoService.translate(this.i18nKeys.viewAssignment), kind: 'action' },
      options: kebabOptions,
    } as unknown as TableRow;
  }

  private getStatusBadge(status: string | null): { kind: QaiBadgeKind; label: string } | null {
    if (!status) return null;
    // SAFE: internal status enum switch
    switch (status.toLowerCase()) {
      case 'staged':   return { kind: 'yellow',            label: this.translocoService.translate(this.i18nKeys.statusStaged)  };
      case 'active':   return { kind: 'green',             label: this.translocoService.translate(this.i18nKeys.statusActive)  };
      case 'complete': return { kind: 'gray-blue',         label: this.translocoService.translate(this.i18nKeys.statusEnded)   };
      case 'closed':   return { kind: 'gray-blue-outline', label: this.translocoService.translate(this.i18nKeys.statusClosed)  };
      case 'draft':    return { kind: 'yellow',            label: this.translocoService.translate(this.i18nKeys.statusDraft)   };
      default:         return { kind: 'yellow',            label: transformLabel(status) };
    }
  }
}
